import React, { useEffect, useState, useRef, useMemo } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import { debounce } from 'lodash';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import styles from './MapView.module.scss';

// Fix default marker icon paths for webpack/CRA builds
Reflect.deleteProperty(L.Icon.Default.prototype, '_getIconUrl');
L.Icon.Default.mergeOptions({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
});

interface Coords {
  lat: number;
  lng: number;
}

interface MapViewProps {
  street: string;
  zip_code: string;
  city: string;
  country_code?: string;
  className?: string;
}

async function geocodeAddress(
  street: string,
  zip_code: string,
  city: string,
  country_code?: string,
): Promise<Coords | null> {
  try {
    const query = encodeURIComponent(`${street}, ${zip_code} ${city}`);
    const cc = country_code ? `&countrycodes=${encodeURIComponent(country_code)}` : '';
    const url = `https://nominatim.openstreetmap.org/search?q=${query}${cc}&format=json&limit=1`;
    const res = await fetch(url);
    const data = await res.json();
    if (data && data.length > 0) {
      return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    }
    return null;
  } catch {
    return null;
  }
}

const RecenterMap: React.FC<{ coords: Coords }> = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    map.setView([coords.lat, coords.lng], map.getZoom());
  }, [coords.lat, coords.lng, map]);
  return null;
};

// Leaflet only listens to window resizes; when the container changes size on
// its own (sidebar collapse, overlay resize) the tiles have to be re-laid out.
const ResizeMap: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    if (typeof ResizeObserver === 'undefined') return undefined;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => map.invalidateSize());
    });
    observer.observe(container);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [map]);
  return null;
};

const MapView: React.FC<MapViewProps> = ({ street, zip_code, city, country_code, className }) => {
  const [coords, setCoords] = useState<Coords | null>(null);
  const isMounted = useRef(true);

  const debouncedGeocode = useMemo(
    () =>
      debounce(async (s: string, z: string, c: string, cc?: string) => {
        const result = await geocodeAddress(s, z, c, cc);
        if (isMounted.current) {
          setCoords(result);
        }
      }, 1200),
    [],
  );

  useEffect(() => {
    return () => {
      isMounted.current = false;
      debouncedGeocode.cancel();
    };
  }, [debouncedGeocode]);

  useEffect(() => {
    if (!street.trim() || !zip_code.trim() || !city.trim()) {
      setCoords(null);
      return;
    }
    debouncedGeocode(street, zip_code, city, country_code);
  }, [street, zip_code, city, country_code, debouncedGeocode]);

  if (!coords) return null;

  return (
    <div className={`${styles.root} ${className ?? ''}`}>
      <MapContainer
        center={[coords.lat, coords.lng]}
        zoom={15}
        scrollWheelZoom={false}
        style={{ height: '100%', width: '100%' }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={[coords.lat, coords.lng]} />
        <RecenterMap coords={coords} />
        <ResizeMap />
      </MapContainer>
    </div>
  );
};

export default MapView;
